import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { route } from 'ziggy-js';

/*
 * The Resources page's data, all from the CMS through this app's own
 * endpoints (StrapiController): the article list one page at a time, the
 * topics (the CMS categories) and the guides to download. Nothing is
 * fetched more than once for the same question; a failure is reported as a
 * failure, never as "nothing there".
 */

/** How many articles one page of the library asks the server for. */
export const LIBRARY_PAGE_SIZE = 12;

/** The topic that stands for every topic (the server's own name for it). */
export const ALL_TOPICS = 'all';

/** The article page for a slug. */
export const articleHref = (slug) => route('blog.article.get', { slug });

/**
 * The query string for one page of the list: the locale, the page and its
 * size always; the topic and the search only when they narrow the list.
 */
export function articleListParams({ locale, page, pageSize = LIBRARY_PAGE_SIZE, tag = ALL_TOPICS, search = '' }) {
    const params = { locale, page, pageSize };
    if (tag && tag !== ALL_TOPICS) params.tag = tag;
    if (search) params.search = search;

    return params;
}

/** One page of the list, as `{ articles, page, pageCount, total }`. */
export async function fetchArticles(query) {
    const { data } = await axios.get(route('blog.articles.get', articleListParams(query)));
    const pagination = data?.meta?.pagination ?? {};
    const articles = Array.isArray(data?.articles) ? data.articles : [];

    return {
        articles,
        page: pagination.page ?? query.page,
        pageCount: pagination.pageCount ?? (articles.length ? query.page : 0),
        total: pagination.total ?? articles.length,
    };
}

const EMPTY_LIST = { status: 'loading', items: [], page: 0, pageCount: 0, total: 0 };

/**
 * The library: the articles for the chosen topic and search, one page at a
 * time from the server (`showMore` appends the next), plus the featured
 * article, the newest of them all.
 *
 * - `list.status` is 'loading' (a new first page is on its way: the grid
 *   shows skeletons), 'ready' or 'failed' (the error state; `retry` asks
 *   again). `more` is the "Show more" request's own 'idle' | 'loading' |
 *   'failed'.
 * - `featured` is undefined until the unfiltered first page has answered,
 *   then that page's first article (null when there is none, or when the
 *   request failed). It comes from the unfiltered first page wherever it is
 *   asked for, so switching topics before it arrives still sets it.
 * - An answer to a question no longer asked (the topic or search changed,
 *   the language switched) is dropped.
 */
export function useArticleLibrary({ locale, tag, search }) {
    const [list, setList] = useState(EMPTY_LIST);
    const [more, setMore] = useState('idle');
    const [featured, setFeatured] = useState(undefined);
    const [attempt, setAttempt] = useState(0);
    const request = useRef(0);
    const localeRef = useRef(locale);
    localeRef.current = locale;

    // A new language starts over, featured article included.
    useEffect(() => {
        setFeatured(undefined);
    }, [locale]);

    useEffect(() => {
        const id = ++request.current;
        const unfiltered = tag === ALL_TOPICS && !search;
        setList(EMPTY_LIST);
        setMore('idle');

        fetchArticles({ locale, page: 1, tag, search }).then(
            (result) => {
                if (unfiltered && localeRef.current === locale) setFeatured(result.articles[0] ?? null);
                if (id !== request.current) return;
                setList({ status: 'ready', items: result.articles, page: result.page, pageCount: result.pageCount, total: result.total });
            },
            () => {
                if (unfiltered && localeRef.current === locale) setFeatured((current) => current ?? null);
                if (id !== request.current) return;
                setList({ ...EMPTY_LIST, status: 'failed' });
            },
        );
    }, [locale, tag, search, attempt]);

    const retry = useCallback(() => setAttempt((count) => count + 1), []);

    const showMore = useCallback(() => {
        if (list.status !== 'ready' || more === 'loading' || list.page >= list.pageCount) return;
        const id = request.current;
        setMore('loading');

        fetchArticles({ locale, page: list.page + 1, tag, search }).then(
            (result) => {
                if (id !== request.current) return;
                setList((current) => {
                    const seen = new Set(current.items.map((article) => article.slug));

                    return {
                        status: 'ready',
                        items: [...current.items, ...result.articles.filter((article) => !seen.has(article.slug))],
                        page: result.page,
                        pageCount: result.pageCount,
                        total: result.total,
                    };
                });
                setMore('idle');
            },
            () => {
                if (id === request.current) setMore('failed');
            },
        );
    }, [list, more, locale, tag, search]);

    return { list, more, featured, retry, showMore };
}

/**
 * The CMS categories in the active language, for the library's topic tabs,
 * as `{ status, topics }` (status 'loading' | 'ready' | 'failed'); the
 * server's own "all" entry is left out (the page names that tab itself).
 * A new language keeps the previous tabs on screen until its own arrive;
 * a failure leaves none.
 */
export function useTopics(locale) {
    const [state, setState] = useState({ status: 'loading', topics: [] });

    useEffect(() => {
        let current = true;
        setState((previous) => ({ ...previous, status: 'loading' }));
        axios.get(route('blog.tags.get', { locale })).then(
            ({ data }) => {
                if (!current) return;
                const list = Array.isArray(data) ? data : [];
                setState({ status: 'ready', topics: list.filter((topic) => topic?.slug && topic.slug !== ALL_TOPICS && topic.name) });
            },
            () => {
                if (current) setState({ status: 'failed', topics: [] });
            },
        );

        return () => {
            current = false;
        };
    }, [locale]);

    return state;
}

/**
 * The guides to download (the CMS `resources` of type guide): `{ status,
 * guides }`, status 'loading' | 'ready' | 'failed'.
 */
export function useGuides(locale) {
    const [state, setState] = useState({ status: 'loading', guides: [] });

    useEffect(() => {
        let current = true;
        setState({ status: 'loading', guides: [] });
        axios.get(route('blog.resources.get', { locale, page: 1 })).then(
            ({ data }) => {
                if (current) setState({ status: 'ready', guides: Array.isArray(data?.resources) ? data.resources : [] });
            },
            () => {
                if (current) setState({ status: 'failed', guides: [] });
            },
        );

        return () => {
            current = false;
        };
    }, [locale]);

    return state;
}
